import { appTheme } from '../theme/appTheme';

/** Brands without a registered colour fall back to the app's primary, not MUI's old default blue. */
const FALLBACK_BRAND_COLOR = appTheme.palette.primary.main;

interface BrandInfo {
  logo: string;
  name: string;
  color: string;
}

// Local paths only — no external URLs that can be blocked. A brand with '' has
// no local file and resolves through the brand registry's logoUrl, then falls
// back to a colored initial avatar (see resolveBrandLogo / BrandMark).
//
// Adding a file to frontend/public/logos is not enough on its own: it has to be
// referenced here too, or nothing ever loads it.
// Third-party brand colours: these belong to the manufacturers, not the
// theme, so they are the one sanctioned set of literals outside theme/.
/* eslint-disable no-restricted-syntax */
export const vendorLogos: Record<string, BrandInfo> = {
  // --- Brands with a local logo asset ---
  sony:       { logo: '/logos/sony.svg',       name: 'Sony',      color: '#000000' },
  nikon:      { logo: '/logos/nikon.svg',      name: 'Nikon',     color: '#FFD700' },
  panasonic:  { logo: '/logos/Lumix.svg',      name: 'Panasonic', color: '#0050A0' },
  lumix:      { logo: '/logos/Lumix.svg',      name: 'Lumix',     color: '#0050A0' },
  canon:      { logo: '/logos/Canon.jpg',      name: 'Canon',     color: '#CC0000' },
  fujifilm:   { logo: '/logos/fujifilm.png',   name: 'Fujifilm',  color: '#00A651' },
  promaster:  { logo: '/logos/promaster.jpg',  name: 'ProMaster', color: FALLBACK_BRAND_COLOR },
  ricoh:      { logo: '/logos/ricoh.svg',      name: 'Ricoh',     color: '#D7063B' },
  sigma:      { logo: '/logos/sigma.svg',      name: 'Sigma',     color: '#333333' },
  smallrig:   { logo: '/logos/smallrig.svg',   name: 'SmallRig',  color: '#E61E28' },
  tamron:     { logo: '/logos/tamron.svg',     name: 'Tamron',    color: '#005CA9' },
  hasselblad: { logo: '/logos/hasselblad.svg', name: 'Hasselblad', color: '#0C3C60' },
  olympus:    { logo: '/logos/olympus.svg',    name: 'Olympus',   color: '#08298A' },
  omsystem:   { logo: '/logos/omsystem.svg',   name: 'OM System', color: '#08298A' },
  benro:      { logo: '/logos/benro.png',      name: 'Benro',     color: '#0066CC' },
  nisi:       { logo: '/logos/nisi.svg',       name: 'NiSi',      color: '#C41230' },
  tokina:     { logo: '/logos/tokina.svg',     name: 'Tokina',    color: '#1A1A1A' },
  hoya:       { logo: '/logos/hoya.svg',       name: 'Hoya',      color: '#0055A5' },
  rokinon:    { logo: '/logos/samyang.png',    name: 'Rokinon',   color: '#E85D04' },
  samyang:    { logo: '/logos/samyang.png',    name: 'Samyang',   color: '#E85D04' },
  viltrox:    { logo: '/logos/viltrox.png',    name: 'Viltrox',   color: '#1A1A1A' },

  // --- Camera brands with no local asset yet ---
  leica:      { logo: '', name: 'Leica',       color: '#E30613' },
  pentax:     { logo: '', name: 'Pentax',      color: '#D50032' },
  zeiss:      { logo: '', name: 'Zeiss',       color: '#003DA5' },

  // --- Action / drone / specialty ---
  gopro:      { logo: '', name: 'GoPro',       color: '#00A1E4' },
  dji:        { logo: '', name: 'DJI',         color: '#333333' },
  insta360:   { logo: '', name: 'Insta360',    color: '#FFB800' },

  // --- Lighting & studio ---
  godox:      { logo: '', name: 'Godox',       color: '#E8390E' },
  profoto:    { logo: '', name: 'Profoto',     color: '#000000' },
  elinchrom:  { logo: '', name: 'Elinchrom',   color: '#D4213D' },
  broncolor:  { logo: '', name: 'Broncolor',   color: '#1A1A1A' },

  // --- Tripods & support ---
  manfrotto:  { logo: '', name: 'Manfrotto',   color: '#D40000' },
  gitzo:      { logo: '', name: 'Gitzo',       color: '#1A1A1A' },
  mefoto:     { logo: '', name: 'MeFOTO',      color: '#E91E63' },
  arcaswiss:  { logo: '', name: 'Arca-Swiss',  color: '#8B0000' },
  peakdesign: { logo: '', name: 'Peak Design', color: '#222222' },

  // --- Filters & accessories ---
  bw:         { logo: '', name: 'B+W',         color: '#003366' },
  cokin:      { logo: '', name: 'Cokin',       color: '#FF6600' },
  tiffen:     { logo: '', name: 'Tiffen',      color: '#002B5C' },

  // --- Bags & storage ---
  lowepro:    { logo: '', name: 'Lowepro',     color: '#1A1A1A' },
  thinktank:  { logo: '', name: 'Think Tank',  color: '#4E8B3B' },
  tenba:      { logo: '', name: 'Tenba',       color: '#000000' },
  pelican:    { logo: '', name: 'Pelican',     color: '#FFB300' },

  // --- Monitors & display ---
  atomos:     { logo: '', name: 'Atomos',      color: '#FF0000' },
  smallhd:    { logo: '', name: 'SmallHD',     color: '#0087C6' },

  // --- Memory & power ---
  sandisk:    { logo: '', name: 'SanDisk',     color: '#ED1C24' },
  lexar:      { logo: '', name: 'Lexar',       color: '#005BAC' },

  // --- Misc ---
  fotosource: { logo: '', name: 'FotoSource',  color: '#2E7D32' },
  sekonic:    { logo: '', name: 'Sekonic',     color: '#003366' },
  datacolor:  { logo: '', name: 'Datacolor',   color: '#00A859' },
  xrite:      { logo: '', name: 'X-Rite',      color: '#E31937' },
  rode:       { logo: '', name: 'Rode',        color: '#D40000' },
  deity:      { logo: '', name: 'Deity',       color: '#F5C518' },

  default:    { logo: '', name: 'Camera',      color: FALLBACK_BRAND_COLOR },
};
/* eslint-enable no-restricted-syntax */

export const dataIngestionLogos: Record<string, BrandInfo> = {
  shopify: {
    logo: '',
    name: 'Shopify',
    // Shopify's own brand green, not a theme colour.
    // eslint-disable-next-line no-restricted-syntax
    color: '#96BF48',
  },
  sony: vendorLogos.sony,
  nikon: vendorLogos.nikon,
  canon: vendorLogos.canon,
  fujifilm: vendorLogos.fujifilm,
  default: {
    logo: '',
    name: 'Import',
    color: FALLBACK_BRAND_COLOR,
  },
};

/**
 * Look up brand info by name. Falls back to a colored initial if no local logo exists.
 * Prefers local logo paths over any external URL the database might return.
 */
export const getVendorInfo = (brand?: string | null, isDataIngestion = false): BrandInfo => {
  const logos = isDataIngestion ? dataIngestionLogos : vendorLogos;
  const normalizedBrand = String(brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  if (logos[normalizedBrand]) {
    return logos[normalizedBrand];
  }

  for (const [key, value] of Object.entries(logos)) {
    if (normalizedBrand.includes(key) || key.includes(normalizedBrand)) {
      return value;
    }
  }

  return logos.default;
};

/**
 * Wikimedia production thumbnail widths. Direct hotlinks of any other size
 * are rejected with HTTP 400 ("Use thumbnail sizes listed on https://w.wiki/GHai").
 * @see https://www.mediawiki.org/wiki/Common_thumbnail_sizes
 */
export const WIKIMEDIA_THUMB_STEPS = [
  20, 40, 60, 120, 250, 330, 500, 960, 1280, 1920, 3840,
] as const;

const WIKIMEDIA_THUMB_RE =
  /^(https:\/\/upload\.wikimedia\.org\/wikipedia\/[^?#]+\/thumb\/.+\/)(\d+)px-([^/?#]+)([?#].*)?$/i;

/**
 * Rewrite a Wikimedia Commons thumb URL to a width Wikimedia will serve.
 * Non-Wikimedia URLs, original (non-thumb) files, and already-valid sizes
 * are returned unchanged.
 */
export function normalizeWikimediaThumbUrl(url: string): string {
  const match = WIKIMEDIA_THUMB_RE.exec(url);
  if (!match) return url;
  const [, prefix, widthStr, rest, suffix = ''] = match;
  const width = Number(widthStr);
  if ((WIKIMEDIA_THUMB_STEPS as readonly number[]).includes(width)) {
    return url;
  }
  const step =
    WIKIMEDIA_THUMB_STEPS.find((candidate) => candidate >= width) ??
    WIKIMEDIA_THUMB_STEPS[WIKIMEDIA_THUMB_STEPS.length - 1];
  return `${prefix}${step}px-${rest}${suffix}`;
}

/**
 * Resolve a logo URL for a brand. Prefers local logos from vendorLogos,
 * then falls back to the database-stored URL if available.
 */
export const resolveBrandLogo = (brandName: string, dbLogoUrl?: string): string => {
  const info = getVendorInfo(brandName);
  if (info.logo) {
    return info.logo;
  }
  if (dbLogoUrl) {
    return normalizeWikimediaThumbUrl(dbLogoUrl);
  }
  return '';
};

/** Ink used on brand-colored initial avatars. Near-black rather than pure
 *  black so a light brand color does not read as a harsh cutout. */
// Contrast-tuned against every brand colour above (the test pins the
// exceptions), so these stay literal rather than tracking the theme ink.
/* eslint-disable no-restricted-syntax */
export const BRAND_INITIAL_DARK = '#111111';
export const BRAND_INITIAL_LIGHT = '#ffffff';
/* eslint-enable no-restricted-syntax */

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** WCAG relative luminance for a validated #rrggbb color. */
function relativeLuminance(hex: string): number {
  const value = hex.slice(1);
  const channel = (offset: number): number => {
    const srgb = parseInt(value.slice(offset, offset + 2), 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/**
 * WCAG contrast ratio between two #rrggbb colors (1–21).
 *
 * Throws on anything else rather than returning NaN. Every comparison against
 * NaN is false, so a malformed color silently reads as passing a
 * `ratio < threshold` check — which would have let a bad brand color slip
 * through the contrast test below as if it were fine. Failing loudly is the
 * point of that test.
 */
export function contrastRatio(a: string, b: string): number {
  [a, b].forEach((color) => {
    if (!HEX_COLOR.test(color)) {
      throw new Error(`contrastRatio expects #rrggbb colors, received ${JSON.stringify(color)}`);
    }
  });
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Readable ink for text sitting on a brand color.
 *
 * The initial avatars used to hardcode white, which is unreadable on the
 * lighter brand colors — Insta360 (#FFB800) and Pelican (#FFB300) landed
 * around 1.7:1, well under the 4.5:1 WCAG AA needs for text this size.
 * Picking by contrast instead of assuming white fixes that for every brand
 * whose color clears the threshold against one ink or the other.
 *
 * MeFOTO (#E91E63) and SanDisk (#ED1C24) sit near 4.3:1 against both inks, so
 * they land just short whichever way this goes; fixing those two means
 * adjusting the brand colors, which is a design call and not made here.
 * vendorLogos.test.ts pins that list so it cannot grow unnoticed.
 */
export function readableInkOn(backgroundHex: string): string {
  // Guarded here rather than letting contrastRatio throw: this runs during
  // render, where a bad color should degrade to white, not blank the page.
  if (!HEX_COLOR.test(backgroundHex)) return BRAND_INITIAL_LIGHT;
  return contrastRatio(backgroundHex, BRAND_INITIAL_DARK) >=
    contrastRatio(backgroundHex, BRAND_INITIAL_LIGHT)
    ? BRAND_INITIAL_DARK
    : BRAND_INITIAL_LIGHT;
}
